import React from 'react';

/** 테두리형(기본) 색상. 아이콘만 있을 때 쓰던 색을 라벨 버튼에서도 그대로 유지한다. */
const outlineColor = {
  red: 'border-red-500 bg-white text-red-500 hover:bg-red-50',
  // orange 는 tailwind 설정에서 단일 색으로 덮여 있어 hover 틴트를 직접 지정한다.
  orange: 'border-orange4 bg-white text-orange4 hover:bg-[#FF6D2C0D]',
  skyblue: 'border-skyblue bg-white text-skyblue hover:bg-skyblue_5',
  purple: 'border-purple5 bg-white text-purple5 hover:bg-purple0',
  gray: 'border-gray5 bg-white text-gray5 hover:bg-gray1',
} as const;

/** 채움형 색상. 현재 켜져 있는 모드처럼 강조가 필요할 때 쓴다. */
const filledColor = {
  red: 'border-red-500 bg-red-500 text-white hover:border-red-600 hover:bg-red-600',
  orange:
    'border-orange4 bg-orange4 text-white hover:border-orange5 hover:bg-orange5',
  skyblue: 'border-skyblue bg-skyblue text-white hover:opacity-90',
  purple:
    'border-purple5 bg-purple5 text-white hover:border-purple6 hover:bg-purple6',
  gray: 'border-gray5 bg-gray5 text-white hover:border-gray6 hover:bg-gray6',
} as const;

const disabledStyle = 'cursor-not-allowed border-gray3 bg-gray2 text-gray4';

interface ActionButtonProps {
  label: string;
  /** heroicons 처럼 className 을 받는 아이콘 컴포넌트. 없으면 라벨만 그린다. */
  icon?: React.ComponentType<{ className?: string }>;
  color: keyof typeof outlineColor;
  /** filled 는 배경까지 채워 현재 켜진 상태를 강조한다. 기본은 테두리형. */
  variant?: 'outline' | 'filled';
  onClick: () => void;
  disabled?: boolean;
  /** 라벨 길이가 상태에 따라 달라지는 버튼의 너비를 고정할 때 사용한다. */
  className?: string;
}

/** 테이블 관리 영역용 라벨(+아이콘) 버튼 */
const ActionButton = ({
  label,
  icon: Icon,
  color,
  variant = 'outline',
  onClick,
  disabled = false,
  className = '',
}: ActionButtonProps) => {
  // 비활성일 때는 hover 색이 남지 않도록 색상 클래스 자체를 바꿔 준다.
  const colorStyle = disabled
    ? disabledStyle
    : (variant === 'filled' ? filledColor : outlineColor)[color];

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={`inline-flex items-center justify-center gap-1 whitespace-nowrap rounded border px-2 py-1 text-xs font-bold transition-colors ${colorStyle} ${className}`}
    >
      {Icon && <Icon className="h-4 w-4 shrink-0" />}
      {label}
    </button>
  );
};

export default ActionButton;
